import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CloudUpload as UploadIcon,
  Language as LanguageIcon,
  GitHub as GitHubIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useAuth } from '../App';
import { useTranslation } from 'react-i18next';

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

// Helper function to convert HTTP URL to WebSocket URL
const getWebSocketUrl = (baseUrl: string) => {
  return baseUrl.replace(/^http/, 'ws')
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        backgroundColor: '#f8fafc',
        color: '#1e293b',
        p: 3,
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
        fontSize: '0.875rem',
        lineHeight: 1.6,
        overflow: 'auto',
        maxHeight: '500px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Chip
          label={language}
          size="small"
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: 2,
          }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
          <IconButton
            onClick={handleCopy}
            size="small"
            sx={{ 
              color: '#64748b',
              backgroundColor: copied ? '#dcfce7' : 'transparent',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                color: '#1976d2'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <code>{code}</code>
      </pre>
    </Paper>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
  icon,
  title,
  description,
}) => (
  <Card
    sx={{
      height: '100%',
      background: 'linear-gradient(135deg, rgba(0, 207, 243, 0.05), rgba(0, 102, 204, 0.05))',
      border: '1px solid rgba(0, 207, 243, 0.2)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 207, 243, 0.15)',
      },
    }}
  >
    <CardContent sx={{ p: 4, textAlign: 'center' }}>
      <Box sx={{ color: '#1976d2', mb: 3, '& svg': { fontSize: 48 } }}>{icon}</Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1a237e', fontSize: '1.25rem' }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: '#546e7a', lineHeight: 1.6, fontSize: '0.95rem' }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const DeveloperDocs: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [userApiKey, setUserApiKey] = useState<string>('YOUR_API_KEY');
  const auth = useAuth();
  const { t } = useTranslation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const baseURL = API_BASE;

  // Fetch user's API keys on component mount
  useEffect(() => {
    const fetchApiKeys = async () => {
      if (!auth?.user) return;
      
      try {
        const token = await auth.getToken();
        const response = await fetch(`${baseURL}/api-keys`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.keys && data.keys.length > 0) {
            // Use the most recent active API key for examples
            const activeKey = data.keys.find(key => key.is_active);
            if (activeKey) {
              setUserApiKey(`vx_${activeKey.key_suffix}`);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch API keys:', error);
        // Keep default placeholder if fetch fails
      }
    };

    fetchApiKeys();
  }, [auth, baseURL]);

  const curlExample = `# File upload transcription
curl -X POST "${baseURL}/transcription" \\
  -H "X-LVL-KEY: ${userApiKey}" \\
  -H "Content-Type: multipart/form-data" \\
  -F "file_channel1=@audio.wav" \\
  -F "config={
    \\"lang\\": \\"fr-FR\\",
    \\"sample_rate\\": 16000
  }"

# Get transcription result
curl -X GET "${baseURL}/transcription/{transcription_id}" \\
  -H "X-LVL-KEY: ${userApiKey}"`;

  const nodeExample = `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

async function transcribeAudio(apiKey, audioFilePath) {
  try {
    // Upload audio file for transcription
    const formData = new FormData();
    formData.append('file_channel1', fs.createReadStream(audioFilePath));
    formData.append('config', JSON.stringify({
      lang: 'fr-FR',
      sample_rate: 16000
    }));

    const uploadResponse = await axios.post('${baseURL}/transcription', formData, {
      headers: {
        'X-LVL-KEY': apiKey,
        ...formData.getHeaders()
      }
    });

    console.log('Transcription result:', uploadResponse.data);
    
    // If async transcription, poll for results
    if (uploadResponse.data.id) {
      const transcriptionId = uploadResponse.data.id;
      
      const resultResponse = await axios.get(\`${baseURL}/transcription/\${transcriptionId}\`, {
        headers: {
          'X-LVL-KEY': apiKey
        }
      });
      
      console.log('Final result:', resultResponse.data);
    }
    
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
}

// Usage
transcribeAudio('${userApiKey}', 'path/to/audio.wav');`;

  const pythonExample = `import requests
import json

def transcribe_audio(api_key, audio_file_path):
    """
    Transcribe audio file using Voxist API
    """
    base_url = "${baseURL}"
    
    # Prepare the request
    headers = {
        'X-LVL-KEY': api_key
    }
    
    # Prepare form data
    with open(audio_file_path, 'rb') as f:
        files = {
            'file_channel1': f
        }
        data = {
            'config': json.dumps({
                'lang': 'fr-FR',
                'sample_rate': 16000
            })
        }
        
        try:
            # Upload for transcription
            response = requests.post(
                f'{base_url}/transcription',
                headers=headers,
                files=files,
                data=data
            )
            
            response.raise_for_status()
            result = response.json()
            
            print('Transcription result:', result)
            
            # If async transcription, get the result
            if 'id' in result:
                transcription_id = result['id']
                
                # Poll for results
                result_response = requests.get(
                    f'{base_url}/transcription/{transcription_id}',
                    headers=headers
                )
                
                result_response.raise_for_status()
                final_result = result_response.json()
                
                print('Final result:', final_result)
                return final_result
            
            return result
            
        except requests.exceptions.RequestException as e:
            print(f'Error: {e}')
            return None

# Usage example
if __name__ == "__main__":
    api_key = "${userApiKey}"
    audio_file = "path/to/audio.wav"
    
    result = transcribe_audio(api_key, audio_file)`;

  const websocketExample = `// Real-time WebSocket transcription
const WebSocket = require('ws');

function connectWebSocket(token, language = 'fr-FR') {
  const ws = new WebSocket(\`\${getWebSocketUrl(API_BASE)}/ws?token=\${encodeURIComponent(token)}\`);
  
  ws.on('open', () => {
    console.log('WebSocket connected');
    
    // Send configuration
    ws.send(JSON.stringify({
      config: {
        lang: language,
        sample_rate: 16000
      }
    }));
  });
  
  ws.on('message', (data) => {
    try {
      const message = JSON.parse(data.toString());
      
      if (message.type === 'partial') {
        console.log('Partial:', message.text);
      } else if (message.type === 'final') {
        console.log('Final:', message.text);
      }
    } catch (error) {
      // Handle binary data or non-JSON messages
    }
  });
  
  ws.on('error', (error) => {
    console.error('WebSocket error:', error);
  });
  
  return ws;
}

// Usage
const token = "YOUR_JWT_TOKEN";
const ws = connectWebSocket(token, 'fr-FR');`;

  return (
    <Container maxWidth="lg" sx={{ py: 6, minHeight: '100vh', background: 'linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%)' }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 8, py: 6, px: 4, borderRadius: 4, background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)', boxShadow: '0 2px 12px rgba(0, 102, 204, 0.06)' }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #1976d2, #1565c0, #0d47a1)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            letterSpacing: '-0.02em',
          }}
        >
          {t('developerDocs')}
        </Typography>
        <Typography
          variant="h5"
          sx={{ mb: 6, fontWeight: 500, color: '#546e7a', fontSize: { xs: '1.2rem', md: '1.5rem' }, lineHeight: 1.4 }}
        >
          {t('developerDocsSubtitle')}
        </Typography>
        
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<GitHubIcon />}
            href="https://github.com/Voxist/asr-websocket-samples"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: 'linear-gradient(45deg, #00CFF3, #00B8D4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00B8D4, #0097A7)',
              },
            }}
          >
            {t('websocketSamples')}
            <LaunchIcon sx={{ ml: 1, fontSize: 16 }} />
          </Button>
          <Button
            variant="outlined"
            startIcon={<ApiIcon />}
            sx={{
              borderColor: '#00CFF3',
              color: '#00CFF3',
              '&:hover': {
                borderColor: '#00B8D4',
                backgroundColor: 'rgba(0, 207, 243, 0.1)',
              },
            }}
          >
            {t('apiReference')}
          </Button>
        </Stack>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<SpeedIcon sx={{ fontSize: 40 }} />}
            title={t('realtimeProcessing')}
            description={t('realtimeDescription')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<LanguageIcon sx={{ fontSize: 40 }} />}
            title={t('multiLanguageSupport')}
            description={t('multiLanguageDescription')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={<SecurityIcon sx={{ fontSize: 40 }} />}
            title={t('secureReliable')}
            description={t('secureReliableDescription')}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '1px solid #e8f4fd',
          boxShadow: '0 4px 20px rgba(0, 102, 204, 0.08)',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: '#e8f4fd' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="API documentation tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 72,
                fontSize: '1rem',
                color: '#546e7a',
                '&:hover': {
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              },
              '& .Mui-selected': {
                color: '#1976d2 !important',
                fontWeight: 700,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab label={t('quickStart')} />
            <Tab label={t('curlExamples')} />
            <Tab label={t('nodeJsIntegration')} />
            <Tab label={t('pythonIntegration')} />
            <Tab label={t('websocketRealtime')} />
            <Tab label={t('authentication')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
              {t('quickStartGuide')}
            </Typography>
            
            <Alert
              severity="info"
              sx={{
                mb: 6,
                backgroundColor: '#e3f2fd',
                border: '1px solid #bbdefb',
                borderRadius: 3,
                '& .MuiAlert-icon': {
                  color: '#1976d2',
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, color: '#1a237e' }}>{t('getStartedMinutes')}</AlertTitle>
              <Typography sx={{ color: '#546e7a' }}>
                {t('followStepsIntegrate')}
              </Typography>
            </Alert>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700, color: '#1a237e' }}>
                  <SecurityIcon sx={{ mr: 2, color: '#1976d2', fontSize: 28 }} />
                  1. {t('getApiKey')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', lineHeight: 1.7 }}>
                  {t('getApiKeyDescription')}
                </Typography>

                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700, color: '#1a237e' }}>
                  <UploadIcon sx={{ mr: 2, color: '#1976d2', fontSize: 28 }} />
                  2. {t('uploadAudioFiles')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', lineHeight: 1.7 }}>
                  {t('uploadAudioDescription')}
                </Typography>

                <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', fontWeight: 700, color: '#1a237e' }}>
                  <CodeIcon sx={{ mr: 2, color: '#1976d2', fontSize: 28 }} />
                  3. {t('integrateRealtime')}
                </Typography>
                <Typography variant="body1" sx={{ color: '#546e7a', lineHeight: 1.7 }}>
                  {t('integrateRealtimeDescription')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1a237e' }}>
                  {t('supportedLanguages')}
                </Typography>
                <List dense>
                  {[
                    { code: 'fr-FR', name: 'French' },
                    { code: 'fr-medicalV2-32', name: 'Medical V2' },
                    { code: 'en-US', name: 'English' },
                    { code: 'de-DE', name: 'German' },
                    { code: 'es-ES', name: 'Spanish' },
                    { code: 'it-IT', name: 'Italian' },
                    { code: 'pt-PT', name: 'Portuguese' },
                    { code: 'nl-NL', name: 'Dutch' },
                    { code: 'sv-SE', name: 'Swedish' },
                  ].map((lang) => (
                    <ListItem key={lang.code} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={lang.name}
                        secondary={lang.code}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
            {t('curlExamplesTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('curlDescription')}
          </Typography>
          <CodeBlock code={curlExample} language="bash" />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
            {t('nodeJsTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('nodeJsDescription')}
          </Typography>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 4, 
              backgroundColor: '#e8f5e8', 
              border: '1px solid #4caf50',
              borderRadius: 3,
              '& .MuiAlert-icon': { color: '#2e7d32' },
              '& code': { 
                backgroundColor: '#ffffff',
                padding: '4px 8px',
                borderRadius: 2,
                fontFamily: 'Monaco, Menlo, monospace',
                fontSize: '0.9rem',
                color: '#2e7d32',
                fontWeight: 600
              }
            }}
          >
            <code>npm install axios form-data</code>
          </Alert>
          <CodeBlock code={nodeExample} language="javascript" />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
            {t('pythonTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('pythonDescription')}
          </Typography>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 4, 
              backgroundColor: '#e8f5e8', 
              border: '1px solid #4caf50',
              borderRadius: 3,
              '& .MuiAlert-icon': { color: '#2e7d32' },
              '& code': { 
                backgroundColor: '#ffffff',
                padding: '4px 8px',
                borderRadius: 2,
                fontFamily: 'Monaco, Menlo, monospace',
                fontSize: '0.9rem',
                color: '#2e7d32',
                fontWeight: 600
              }
            }}
          >
            <code>pip install requests</code>
          </Alert>
          <CodeBlock code={pythonExample} language="python" />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
            {t('websocketTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('websocketDescription')}
          </Typography>
          
          <Alert
            severity="success"
            sx={{ 
              mb: 4,
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50',
              borderRadius: 3,
              '& .MuiAlert-icon': { color: '#2e7d32' }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                href="https://github.com/Voxist/asr-websocket-samples"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#2e7d32',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(46, 125, 50, 0.1)'
                  }
                }}
              >
                {t('viewMoreExamples')}
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 700, color: '#1b5e20' }}>{t('completeWebsocketSamples')}</AlertTitle>
            <Typography sx={{ color: '#2e7d32' }}>
              {t('checkGithubRepo')}
            </Typography>
          </Alert>

          <CodeBlock code={websocketExample} language="javascript" />

          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1a237e' }}>
              {t('websocketProtocol')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#546e7a', lineHeight: 1.7, '& strong': { color: '#1a237e', fontWeight: 600 }, '& code': { backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: 1, fontFamily: 'Monaco, Menlo, monospace' } }}>
              <strong>{t('connectionUrl')}:</strong> <code>{getWebSocketUrl(API_BASE)}/ws?token=YOUR_JWT_TOKEN</code>
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#546e7a', lineHeight: 1.7, '& strong': { color: '#1a237e', fontWeight: 600 } }}>
              <strong>{t('audioFormat')}:</strong> {t('audioFormatDetails')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a', lineHeight: 1.7, '& strong': { color: '#1a237e', fontWeight: 600 } }}>
              <strong>{t('messageTypes')}:</strong> {t('messageTypesDetails')}
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a237e', fontSize: '2.25rem' }}>
            {t('authenticationTitle')}
          </Typography>
          
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1a237e' }}>
            {t('apiKeyAuthentication')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('apiKeyUsage')}
          </Typography>
          
          <CodeBlock
            code={`X-LVL-KEY: ${userApiKey}`}
            language="http"
          />

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1a237e' }}>
            {t('jwtTokenAuthentication')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {t('jwtDescription')}
          </Typography>

          <CodeBlock
            code={`curl -X POST "${baseURL}/oauth/token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "grant_type": "client_credentials",
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET"
  }'`}
            language="bash"
          />

          <Alert 
            severity="warning" 
            sx={{ 
              mt: 4,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: 3,
              '& .MuiAlert-icon': { color: '#f57c00' }
            }}
          >
            <AlertTitle sx={{ fontWeight: 700, color: '#e65100' }}>{t('securityBestPractices')}</AlertTitle>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', color: '#f57c00', '& li': { mb: 1, lineHeight: 1.6 } }}>
              <li>{t('securityTip1')}</li>
              <li>{t('securityTip2')}</li>
              <li>{t('securityTip3')}</li>
              <li>{t('securityTip4')}</li>
            </Box>
          </Alert>
        </TabPanel>
      </Paper>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 8, pt: 6, borderTop: '2px solid #e8f4fd', borderRadius: '2px 2px 0 0' }}>
        <Typography variant="body1" sx={{ color: '#546e7a', fontSize: '1.1rem', lineHeight: 1.6 }}>
          {t('needHelp')}{' '}
          <Button
            color="primary"
            href="https://github.com/Voxist/asr-websocket-samples"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              textTransform: 'none', 
              p: 0, 
              minWidth: 'auto',
              fontWeight: 600,
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            {t('githubSamples')}
          </Button>{' '}
          {t('forMoreExamples')}
        </Typography>
      </Box>
    </Container>
  );
};

export default DeveloperDocs;